/* globals confirm */

import React, { useState } from 'react';
import { css } from '@emotion/react';
import { ContextMenu, contextStyles } from '@birdi/context-menu/ContextMenu';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useNavigate } from 'react-router';
import { format, parseISO } from 'date-fns';
import { ResponsiveTable } from '../../common/responsive-table';
import { useMetaSelector } from '../../meta/reducer';
import { Link } from 'react-router';
import { stroot, organisationUsers } from '@birdi/js-sdk/src/public';
import TooltipButton from '@birdi/shared-deprecated/tooltip-button';
import { DeleteUserWarningModal } from './delete-user-warning-modal';

export const UserTable = ({
  users,
  permissions,
  workspaceId = null,
  isMain = false,
}) => {
  const navigate = useNavigate();
  const meta = useMetaSelector();

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [DeletingItemId, setDeletingItemId] = useState(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [invitationSentThisSessionByUserId, setInvitationSentThisSessionByUserId] =
    useState<Record<string, boolean>>({});

  const isWorkspaceAdmin = permissions.includes('workspace:user:create');

  const deleteUserFromOrganisation = (userId: string) => {
    organisationUsers
      .deleteUserFromOrganisation(stroot('to3oom'), userId)
      .then(() => {
        window.location.reload();
      });
  };

  const deleteUserFromWorkspace = (userId: string, orgId: string) => {
    const currentWorkspaceId =
      orgId || workspaceId || meta.workspace.id || meta.organisation.id;
    organisationUsers
      .deleteUserFromWorkspace(stroot('shie9a'), userId, currentWorkspaceId)
      .then(() => {
        window.location.reload();
      });
  };

  const formating = (item) => {
    // console.log('item: ', item);
    let haveWorkspaces = false;
    if (
      item.workspaces &&
      Array.isArray(item.workspaces) &&
      item.workspaces.length
    ) {
      haveWorkspaces = true;
    }
    const rawOrg =
      isMain && haveWorkspaces && item.workspaces?.[0]?.organisationId
        ? item.workspaces[0].organisationId
        : item.organisationId;
    const resendWorkspaceId = Array.isArray(rawOrg)
      ? rawOrg[0]
      : rawOrg ?? workspaceId ?? undefined;

    return {
      id: item.id,
      scopeId:
        isMain && haveWorkspaces ? item.workspaces[0].scopeId : item.scopeId,
      name: `${item.fname || '-'} ${item.lname || '-'}`,
      email: `${item.email}${item.emailVerified ? '' : ' (invited)'}`,
      organisationId:
        isMain && haveWorkspaces
          ? item.workspaces.map((workspace) => workspace.organisationId)
          : item.organisationId,
      organisationName:
        isMain && haveWorkspaces
          ? item.workspaces.map((workspace) => workspace.organisationName)
          : item.organisationName,
      workspaces: item.workspaces?.length || '-',
      roleName:
        isMain && haveWorkspaces
          ? item.workspaces[0].roleName
          : item.roleName || '-',
      hasOpsAccess: item.hasOpsAccess,
      lockedAccount: item.lockedAccount,
      onboardingComplete: item.onboardingComplete,
      createdAt: item.createdAt,
      resendWorkspaceId,
      joined: item.createdAt
        ? format(parseISO(item.createdAt), 'dd MMM yyyy')
        : '-',
    };
  };

  const prepareFormatedData = (item) => {
    const invitationSentThisSession =
      invitationSentThisSessionByUserId[item.id] === true;

    return [
    {
      title: 'Name',
      value: '',
      component: (
        <div
          css={css`
            min-width: 10em;
          `}
        >
          {isWorkspaceAdmin && !workspaceId ? (
            <Link to={`/profile-general/${item.scopeId}`}>
              <span
                css={(theme: BirdiTheme) => css`
                  color: ${theme.deepOcean};
                `}
              >
                {item.name}
              </span>
            </Link>
          ) : (
            <span>{item.name}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Email',
      value: '',
      component: (
        <div
          css={css`
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 15em;
          `}
        >
          {item.email}
        </div>
      ),
      laptopOnly: true,
    },
    {
      title: 'Email',
      value: item.email,
      mobileOnly: true,
    },
    {
      ...(isMain && {
        title: 'Workspace',
        value: '',
        component: (
          <TooltipButton align="start" side="right">
            {{
              trigger: (
                <button
                  type="button"
                  // css={(theme) => css`color: ${theme.deepOcean}`}
                >
                  {item.workspaces}
                </button>
              ),
              label: (
                <div
                  css={(theme) => css`
                    background: ${theme.mono20};
                    padding: 0.25em 0.5em;
                    border-radius: 5px;
                    max-width: 20em;
                    display: inline-flex;
                    flex-wrap: wrap;
                    gap: 0.25em;
                    overflow-wrap: break-word;
                  `}
                >
                  {item.organisationName.map((name, index) => (
                    <span key={index}>
                      {name}
                      {index < item.organisationName.length - 1 && ' | '}
                    </span>
                  ))}
                </div>
              ),
            }}
          </TooltipButton>
        ),
      }),
    },
    {
      title: 'Role',
      value: item.roleName,
    },
    {
      title: 'Type',
      value: '',
      component: (
        <div
          css={(theme: BirdiTheme) => css`
            background-color: ${item.roleName === 'Guest' || item.hasOpsAccess
              ? theme.lightOcean
              : theme.lightFlora};
            color: ${item.roleName === 'Guest' || item.hasOpsAccess
              ? theme.deepOcean
              : theme.flora};
            padding: 0.5em;
            border-radius: 4px;
            max-width: 4em;
            text-align: center;
          `}
        >
          {item.roleName === 'Guest' || item.hasOpsAccess ? 'Free' : 'Paid'}
        </div>
      ),
    },
    {
      title: 'Status',
      value: '',
      component: (
        <div
          css={css`
            min-width: 8.5rem;
          `}
        >
          {item.lockedAccount && <span>Locked</span>}
          {!item.lockedAccount && item.onboardingComplete && <span>Active</span>}
          {!item.lockedAccount && !item.onboardingComplete && (
            <>
              {isWorkspaceAdmin &&
              item.id !== meta.id &&
              item.roleName !== 'Organisation Admin' ? (
                <button
                  type="button"
                  disabled={
                    resendingUserId === item.id || invitationSentThisSession
                  }
                  onClick={() => {
                    setResendingUserId(item.id);
                    organisationUsers
                      .resendInvitation(stroot('ah3bae'), item.id, {
                        ...(item.resendWorkspaceId && {
                          workspaceId: item.resendWorkspaceId,
                        }),
                      })
                      .then(() => {
                        setInvitationSentThisSessionByUserId((prev) => ({
                          ...prev,
                          [item.id]: true,
                        }));
                      })
                      .catch((err: Error & { message?: string }) => {
                        window.alert(
                          err?.message || 'Failed to resend invitation.',
                        );
                      })
                      .finally(() => setResendingUserId(null));
                  }}
                  css={(theme: BirdiTheme) => css`
                    font-size: 0.8125rem;
                    padding: 0.35em 0.65em;
                    border-radius: 4px;
                    background: ${invitationSentThisSession ? theme.flora : theme.deepOcean};
                    color: ${theme.mono0};
                    cursor: pointer;
                    &:disabled {
                      opacity: 0.5;
                      cursor: not-allowed;
                    }
                  `}
                >
                  {invitationSentThisSession
                    ? 'Invitation sent'
                    : 'Resend invitation'}
                </button>
              ) : (
                <span>Pending</span>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Joined',
      value: item.joined,
    },
    {
      title: '',
      value: '',
      component: (
        <div>
          {isWorkspaceAdmin &&
            item.id !== meta.id &&
            item.roleName !== 'Organisation Admin' && (
              <ContextMenu alignRight bottom>
                {(props) => (
                  <div
                    css={(theme: BirdiTheme) => css`
                      ${contextStyles(theme)};
                      width: 8em;
                      button {
                        padding: 0.5em 1em;
                      }
                    `}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/profile-general/${item.scopeId}`);
                        props.hide();
                      }}
                    >
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => {
                        if (isMain) {
                          setDeletingItemId(item.id);
                          setShowWarningModal(true);
                          // confirm('Are you sure you want to delete this user from the whole organisation?') &&
                          //   deleteUserFromOrganisation(item.id);
                        } else {
                          confirm(
                            'Are you sure you want to delete this user from this workspace?',
                          ) &&
                            deleteUserFromWorkspace(
                              item.id,
                              item.organisationId,
                            );
                        }
                        props.hide();
                      }}
                      type="button"
                    >
                      <span>Delete User</span>
                    </button>
                  </div>
                )}
              </ContextMenu>
            )}
        </div>
      ),
      rightAlignContextMenu: true,
    },
  ];
  };
  const data = users.map((item) => formating(item));
  const formatedData = data.map((item) => prepareFormatedData(item));

  return (
    <div>
      <div
        css={(theme: BirdiTheme) => css`
          flex-grow: 1;
          margin: 1em;
          padding: 1em 0;
          border: solid 1px ${theme.mono20};
          border-radius: 10px;
          @media (max-width: ${breakpoints.xs}) {
            border: none;
          }
        `}
      >
        <ResponsiveTable
          data={formatedData}
          tableStyles={css`
            tr {
              border: none;
            }
            th {
              padding: 0 1em 1em 1em;
            }
            td {
              padding: 1em 1em 0 1em;
            }
          `}
        />
      </div>
      <DeleteUserWarningModal
        open={showWarningModal}
        setOpen={(status) => {
          setShowWarningModal(status);
        }}
        deleteConfirm={() => {
          if (DeletingItemId) deleteUserFromOrganisation(DeletingItemId);
        }}
      />
    </div>
  );
};

// role select
// component: ({
//   isSelf, user, permissions, updateRole, seats, loadSeats, guestsOnly,
// }) => (
//   <td
//     key="role"
//     css={css`
//       margin-top: 25px;
//       width: 150px;
//     `}
//   >
//     {isWorkspaceAdmin && (
//       <RoleSelect
//         onChange={(role) => {
//           const roleId = role ? role.value : null;
//           updateRole(user.id, roleId).then(() => {
//             loadSeats();
//           });
//         }}
//         defaultValue={{ label: user.roleName, value: user.roleId }}
//         isDisabled={isSelf || (user.roleName === 'Organisation Admin')}
//         guestsOnly={guestsOnly}
//       />
//     )}
//   </td>
// ),
